import { roundNumber } from '../round-number/'
import toNumber from 'lodash/toNumber'
import lowerCase from 'lodash/lowerCase'

export function mmToInch(mm = 0, decimalPlace = 4) {
  return normalizedResult(mm / 25.4, decimalPlace)
}

//converting from inches to mm needs to be 2 decimal places, only as it is difficult to it
export function inchToMm(inch = 0, decimalPlace = 2) {
  return normalizedResult(inch * 25.4, decimalPlace)
}

export function poundsToOunce(pounds = 0, decimalPlace = 4) {
  return normalizedResult(pounds * 16, decimalPlace)
}

export function ounceToPounds(ounce = 0, decimalPlace = 4) {
  return normalizedResult(ounce / 16, decimalPlace)
}

export function gramsToOunce(grams = 0, decimalPlace = 6) {
  return normalizedResult(grams * 0.035274, decimalPlace)
}

export function gramsToPounds(grams = 0, decimalPlace = 8) {
  return normalizedResult(grams * 0.00220462, decimalPlace)
}

const normalizedResult = (result: number, decimalPlace: number) => {
  const finalResult = roundNumber(result, decimalPlace)

  //if isNan, just return zero, the number may be too small to be considered
  return isNaN(finalResult) ? 0 : finalResult
}

/**
 * This will convert multiple units to ounce, some shipping weights supports only ounce so we need this general convertion tool
 * @param shippingWeight
 * @param unit
 */
export function shippingWeightToOunce(shippingWeight = 0, unit = '') {
  const weightValue = toNumber(shippingWeight ?? 0)
  switch (lowerCase(unit ?? '')) {
    case 'lbs':
    case 'pounds':
    case 'pound':
      return poundsToOunce(weightValue)

    case 'kg':
    case 'kilograms':
    case 'kilogram':
      //convert first to grams then to pounds
      return gramsToOunce(weightValue * 1000)

    case 'g':
    case 'grams':
    case 'gram':
      //convert first to grams then to pounds
      return gramsToOunce(weightValue)

    //fallback to ounce
    default:
      return weightValue
  }
}

// UOM Conversion Utilities
// These are generic conversion functions that use conversion factors rather than hardcoded unit values

/**
 * Converts a quantity from a context UOM to the base UOM.
 * Semantics: 1 [context UOM] = [conversionFactor] [base UOM]
 */
export function convertToBaseUnit(quantity: number, conversionFactor: number, decimalPrecision = 0): number {
  if (conversionFactor <= 0) {
    throw new Error('conversionFactor must be greater than 0')
  }

  return roundNumber(quantity * conversionFactor, decimalPrecision)
}

/**
 * Converts a quantity from the base UOM back to the context UOM.
 * Semantics: 1 [context UOM] = [conversionFactor] [base UOM]
 */
export function convertFromBaseUnit(quantity: number, conversionFactor: number, decimalPrecision = 0): number {
  if (conversionFactor <= 0) {
    throw new Error('conversionFactor must be greater than 0')
  }

  return roundNumber(quantity / conversionFactor, decimalPrecision)
}

/**
 * Calculates a conversion factor between two UOMs given their individual factors.
 * Result = contextUomFactor / baseUomFactor
 */
export function calculateConversionFactor(
  contextUomFactor: number,
  baseUomFactor: number,
  decimalPrecision = 0,
): number {
  if (contextUomFactor <= 0) {
    throw new Error('contextUomFactor must be greater than 0')
  }
  if (baseUomFactor <= 0) {
    throw new Error('baseUomFactor must be greater than 0')
  }

  return roundNumber(contextUomFactor / baseUomFactor, decimalPrecision)
}
